import liff from '@line/liff';
import { isWebAccess } from '@/utils/accessMode';

const LIFF_ID = import.meta.env.VITE_LIFF_ID;
const USE_DUMMY = import.meta.env.VITE_USE_DUMMY_LINE_ID === 'true';
const SHOW_DEBUG = import.meta.env.VITE_SHOW_DEBUG_PANEL === 'true';

// ドメインベースで WEB アクセスと判定される場合は LIFF は一切使わない。
// 順序: ①まずドメインで WEB/LIFF を判定 → ②LIFF 側なら dummy/real を判定。
// これを守らないと WEB ログイン済みでも dummy の LINE USER ID が顔を出す。
function isLiffMode(): boolean {
  return !isWebAccess();
}

const LS_KEY_LINE_USER_ID = 'DUMMY_LOGIN_USER_ID';
const LS_KEY_IS_FRIEND = 'DUMMY_IS_FRIEND_FLAG';
const DEFAULT_DUMMY_LINE_ID = 'U_DUMMY_LINE_USER_001';

function debugLog(...args: unknown[]) {
  if (SHOW_DEBUG) {
    console.log('[LIFF-DUMMY]', ...args);
  }
}

function getDummyLineUserId(): string {
  const stored = localStorage.getItem(LS_KEY_LINE_USER_ID);
  const resolved = stored || DEFAULT_DUMMY_LINE_ID;
  debugLog('getLineUserId:', { stored, resolved });
  return resolved;
}

function getDummyIsFriend(): boolean {
  const stored = localStorage.getItem(LS_KEY_IS_FRIEND);
  const resolved = stored === 'false' ? false : true;
  debugLog('getFriendshipFlag:', { stored, resolved });
  return resolved;
}

let initialized = false;
let initPromise: Promise<void> | null = null;

type LiffPermissionApi = {
  query?: (scope: 'profile' | 'openid' | 'email' | 'chat_message.write') => Promise<{ state: string }>;
  getGrantedAll?: () => Promise<string[]>;
};

type LiffWithPermission = typeof liff & {
  permission?: LiffPermissionApi;
};

export type LiffDiagnostics = {
  isLoggedIn: boolean;
  isInClient: boolean;
  contextType: string | null;
  scopes: string[];
  profilePermissionState: string | null;
  friendshipError: string | null;
};

export async function initializeLiff(): Promise<void> {
  if (initialized) return;

  // ①ドメイン判定：WEB アクセスなら LIFF は触らずフラグだけ立てて終了。
  if (!isLiffMode()) {
    debugLog('[LIFF] Web access - skipping LIFF init entirely');
    initialized = true;
    return;
  }

  // ②LIFF 側のうち、dummy mode なら LIFF SDK を呼ばず終了。
  if (USE_DUMMY || !LIFF_ID) {
    debugLog('[LIFF] Dummy mode - skipping LIFF init');
    initialized = true;
    return;
  }

  // 複数箇所から呼ばれても1回だけ初期化
  if (!initPromise) {
    initPromise = (async () => {
      try {
        // LIFF認証リダイレクト前にパスを保存
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && currentPath !== '/') {
          sessionStorage.setItem('liff_redirect_path', currentPath);
        }

        await liff.init({ liffId: LIFF_ID });
        initialized = true;
        debugLog('[LIFF] Initialized successfully, isLoggedIn:', liff.isLoggedIn(), 'isInClient:', liff.isInClient());

        // 認証後にパスを復元
        const savedPath = sessionStorage.getItem('liff_redirect_path');
        if (savedPath && window.location.pathname === '/') {
          sessionStorage.removeItem('liff_redirect_path');
          window.location.replace(savedPath);
          return;
        }
        sessionStorage.removeItem('liff_redirect_path');

        // LINE内ブラウザでなく、未ログインならログインへリダイレクト
        if (!liff.isLoggedIn()) {
          debugLog('[LIFF] Not logged in, redirecting to LINE login...');
          liff.login({ redirectUri: window.location.href });
          return;
        }
      } catch (error) {
        // 初期化失敗は本番でも console に出す（致命的）
        if (SHOW_DEBUG) console.error('[LIFF] Initialization failed:', error);
        initPromise = null;
        throw error;
      }
    })();
  }

  return initPromise;
}

export function getLineUserId(): string | null {
  // ①ドメイン判定：WEB アクセスなら LINE USER ID は存在しないので常に null。
  //   WEB ユーザの認証は JWT（patient_access_token）で行う。
  if (!isLiffMode()) {
    return null;
  }

  // ②LIFF 側で dummy mode なら localStorage のダミー ID を返す。
  if (USE_DUMMY) {
    return getDummyLineUserId();
  }

  if (!initialized) {
    console.warn('[LIFF] Not initialized');
    return null;
  }

  if (!liff.isLoggedIn()) {
    return null;
  }

  const idToken = liff.getDecodedIDToken();
  return idToken?.sub ?? null;
}

export async function getFriendshipFlag(): Promise<boolean | null> {
  // WEB アクセスでは友だち概念が無いので null。
  if (!isLiffMode()) {
    return null;
  }

  if (USE_DUMMY) {
    return getDummyIsFriend();
  }

  if (!initialized || !liff.isLoggedIn()) {
    return null;
  }

  if (typeof liff.getFriendship !== 'function') {
    return null;
  }

  try {
    const friendship = await liff.getFriendship();
    return friendship.friendFlag;
  } catch (error) {
    console.warn('[LIFF] getFriendship failed:', error);
    return null;
  }
}

export function isDummyLiffMode(): boolean {
  // WEB アクセスなら dummy LIFF は絶対に使わない。
  if (!isLiffMode()) return false;
  return USE_DUMMY || !LIFF_ID;
}

export async function getLiffDiagnostics(): Promise<LiffDiagnostics> {
  if (USE_DUMMY || !LIFF_ID) {
    return {
      isLoggedIn: true,
      isInClient: false,
      contextType: 'dummy',
      scopes: ['openid'],
      profilePermissionState: 'granted',
      friendshipError: null,
    };
  }

  const liffWithPermission = liff as LiffWithPermission;
  const isLoggedIn = initialized && liff.isLoggedIn();
  const isInClient = initialized && liff.isInClient();

  let contextType: string | null = null;
  let scopes: string[] = [];
  let profilePermissionState: string | null = null;
  let friendshipError: string | null = null;

  if (initialized) {
    try {
      const context = liff.getContext();
      contextType = context?.type ?? null;
      scopes = context?.scope ?? [];
    } catch (error) {
      console.warn('[LIFF] getContext failed:', error);
    }
  }

  if (initialized && isLoggedIn && liffWithPermission.permission?.query) {
    try {
      const permission = await liffWithPermission.permission.query('profile');
      profilePermissionState = permission.state;
    } catch (error) {
      console.warn('[LIFF] permission.query(profile) failed:', error);
    }
  }

  if (initialized && isLoggedIn && typeof liff.getFriendship === 'function') {
    try {
      await liff.getFriendship();
    } catch (error) {
      if (error instanceof Error) {
        friendshipError = error.message;
      } else {
        friendshipError = String(error);
      }
    }
  }

  return {
    isLoggedIn,
    isInClient,
    contextType,
    scopes,
    profilePermissionState,
    friendshipError,
  };
}

export function isLiffEnvironment(): boolean {
  if (!isLiffMode()) return false;
  if (USE_DUMMY) return true;
  if (!initialized) return false;
  return liff.isInClient();
}

export function getLiff() {
  return liff;
}
